// src/coordinated-playbook/TemplateChapter.js

import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../Security/firebase-config";
import "../individual-playbook/General-Page.css";
import "./Coordinated-Playbook.css";
import "../utils/ImageHoverEffect.css";
// NOTE: No applyImageZoomEffect here to avoid DOM mutation issues
import Footer from "../components/core-components/Footer";
import Highlight from "../components/core-components/Highlight";
import Favorites from "../components/core-components/Favorites";
import { useBlur } from "../context/BlurContext";
import LazyVideo from "../components/core-components/LazyVideo";
import MovementGuideModal from "../components/core-components/MovementGuideModal";
import useAccordionDoubleClick from "../hooks/useAccordionDoubleClick";

/**
 * ==========================================
 * TEMPLATE MEDIA SOURCES
 * ==========================================
 * This now includes concrete example keys to demo the template.
 */

const mediaSources = {
  /* =========================
   * DEFENSE EXAMPLE
   * ========================= */

  // Sub-accordion: defense-example-accordion
  "defense-example-accordion": {
    "example-overview": {
      label: "Example Overview",
      type: "image",
      src: "/assets/template/example-overview.png"
    },
    "example-reaction1": {
      label: "Example Reaction 1",
      type: "video",
      src: "/assets/template/example-reaction1.mp4",
      props: { ui: "minimal", loop: true }
    },
    "example-reaction2": {
      label: "Example Reaction 2",
      type: "video",
      src: "/assets/template/example-reaction2.mp4"
    }
  },

  /* =========================
   * ATTACK EXAMPLE
   * ========================= */

  // Sub-accordion: attack-example-accordion
  "attack-example-accordion": {
    "attack-overview": {
      label: "Attack Overview",
      type: "image",
      src: "/assets/template/attack-overview.png"
    },
    "attack-reaction1": {
      label: "Attack Reaction 1",
      type: "video",
      src: "/assets/template/attack-reaction1.mp4"
    }
  }
};

/**
 * ==========================================
 * TEMPLATE TEXT CONTENT
 * ==========================================
 * Text changes based on:
 * - sectionId
 * - selected media key
 * - selected role ("general", "agent1", ... "agent5")
 */

const ROLES = ["general", "agent1", "agent2", "agent3", "agent4", "agent5"];

const textContent = {
  "defense-example-accordion": {
    "example-overview": {
      general:
        "Defense / Overview / General: High-level explanation of the setup, default positions, and how the whole team should think about this round.",
      agent1:
        "Defense / Overview / Agent 1: Focus on anchor responsibilities – where to play, what utility to hold, and how to survive the first wave.",
      agent2:
        "Defense / Overview / Agent 2: Describe how this agent supports with utility, re-swings, and off-angles after first contact.",
      agent3:
        "Defense / Overview / Agent 3: Explain lurk/rotate timings and when this agent should leave site or push for info.",
      agent4:
        "Defense / Overview / Agent 4: Outline how this agent trades, holds crossfires, and coordinates with Agent 1.",
      agent5:
        "Defense / Overview / Agent 5: Clarify flex roles, plug gaps in the setup, and watch for mid-round adaptations."
    },
    "example-reaction1": {
      general:
        "Defense / Reaction 1 / General: How the team should react when the enemy hits fast – who fights, who delays, who rotates.",
      agent1:
        "Defense / Reaction 1 / Agent 1: When you hear fast exec, drop your early utility, fall to safe positions, and call for help quickly.",
      agent2:
        "Defense / Reaction 1 / Agent 2: Use your utility to delay the push and create crossfires with Agent 1 instead of solo peeking.",
      agent3:
        "Defense / Reaction 1 / Agent 3: Be ready to flank or fast rotate once you’ve confirmed the hit – timing is more important than kills.",
      agent4:
        "Defense / Reaction 1 / Agent 4: Hold a late off-angle, don’t overfight – let your utility and teammates stall first.",
      agent5:
        "Defense / Reaction 1 / Agent 5: Watch for lurks and cut off rotations so your team doesn’t get pinched while defending."
    },
    "example-reaction2": {
      general:
        "Defense / Reaction 2 / General: Game plan for slow defaults – how to manage utility and information over the full round.",
      agent1:
        "Defense / Reaction 2 / Agent 1: Resist the urge to overpeek; save key utility for the final 20 seconds of the round.",
      agent2:
        "Defense / Reaction 2 / Agent 2: Use small pieces of utility to check space instead of giving up your full kit early.",
      agent3:
        "Defense / Reaction 2 / Agent 3: Probe the map safely for information, but always have an exit route and a teammate nearby.",
      agent4:
        "Defense / Reaction 2 / Agent 4: Anchor a key choke and communicate what you see so the team can adjust rotates.",
      agent5:
        "Defense / Reaction 2 / Agent 5: Fill gaps in the setup – if someone floats for info, make sure the site is still covered."
    }
  },

  "attack-example-accordion": {
    "attack-overview": {
      general:
        "Attack / Overview / General: Basic game plan for the hit – where everyone starts, which areas you want control of, and your win condition.",
      agent1:
        "Attack / Overview / Agent 1: Entry responsibilities – first contact, clearing common angles, and calling site conditions.",
      agent2:
        "Attack / Overview / Agent 2: Second in – trade frags, clear deeper angles, and stabilize site control after the entry.",
      agent3:
        "Attack / Overview / Agent 3: Lurk or map control role – manage timings so the team doesn’t get flanked.",
      agent4:
        "Attack / Overview / Agent 4: Support with flashes/smokes to enable the front line and secure safe plants.",
      agent5:
        "Attack / Overview / Agent 5: Backup utility and post-plant anchor – think about lineups and safe positions."
    },
    "attack-reaction1": {
      general:
        "Attack / Reaction 1 / General: How to adapt when the defense fights early – when to commit, reset, or pivot.",
      agent1:
        "Attack / Reaction 1 / Agent 1: If you’re denied space early, don’t force – call to fall back and re-hit with better utility.",
      agent2:
        "Attack / Reaction 1 / Agent 2: Trade carefully; don’t overextend if utility is gone and your entry has fallen.",
      agent3:
        "Attack / Reaction 1 / Agent 3: Look for punishing over-aggression on the other side of the map after early contact.",
      agent4:
        "Attack / Reaction 1 / Agent 4: Save some utility for the re-commit so your team isn’t dry swinging into utility.",
      agent5:
        "Attack / Reaction 1 / Agent 5: Communicate the bigger picture – whether you have numbers, map control, or time advantage."
    }
  }
};

const TemplateChapter = () => {
  // Team + UI state
  const [userTeamId, setUserTeamId] = useState(null);
  const { isBlurred } = useBlur();
  const [selectedImage, setSelectedImage] = useState("image1");
  const [selectedMediaBySection, setSelectedMediaBySection] = useState({});
  const [selectedRoleBySection, setSelectedRoleBySection] = useState({});
  const [openAccordions, setOpenAccordions] = useState({});
  const [isMovementGuideOpen, setIsMovementGuideOpen] = useState(false);

  useAccordionDoubleClick({ openAccordions, setOpenAccordions });
  const location = useLocation();

  /**
   * ==========================================
   * Auto-expand accordions for highlights
   * ==========================================
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightId = params.get("highlight");

    if (!highlightId) return;

    const accordionMap = {
      // When you visit ?highlight=TEMPLATE-HIGHLIGHT-DEF-EXAMPLE
      "TEMPLATE-HIGHLIGHT-DEF-EXAMPLE": [
        "Template-Defense-Main",
        "defense-example-accordion"
      ],
      // When you visit ?highlight=TEMPLATE-HIGHLIGHT-ATK-EXAMPLE
      "TEMPLATE-HIGHLIGHT-ATK-EXAMPLE": [
        "Template-Attack-Main",
        "attack-example-accordion"
      ]
    };

    const pathsToOpen = accordionMap[highlightId];
    if (!pathsToOpen) return;

    setOpenAccordions(prev => {
      const updated = { ...prev };
      pathsToOpen.forEach(key => {
        updated[key] = true;
      });
      sessionStorage.setItem(
        "templateChapterAccordionState",
        JSON.stringify(updated)
      );
      return updated;
    });

    setTimeout(() => {
      const el = document.querySelector(
        `[data-container-id="${highlightId}"]`
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 500);
  }, [location.search]);

  // Hydrate accordion + media + role from sessionStorage
  useEffect(() => {
    const storedAccordionState = sessionStorage.getItem(
      "templateChapterAccordionState"
    );
    if (storedAccordionState) {
      try {
        const parsed = JSON.parse(storedAccordionState);
        setOpenAccordions(parsed);

        const newMediaState = {};
        Object.entries(parsed).forEach(([accordionId, isOpen]) => {
          if (isOpen && mediaSources[accordionId]) {
            const firstKey = Object.keys(mediaSources[accordionId])[0];
            if (firstKey) {
              newMediaState[accordionId] = firstKey;
            }
          }
        });

        if (Object.keys(newMediaState).length > 0) {
          setSelectedMediaBySection(prev => ({
            ...prev,
            ...newMediaState
          }));
        }
      } catch (e) {
        console.error("Failed to parse accordion state", e);
      }
    }

    const storedRoles = sessionStorage.getItem("templateChapterRoleState");
    if (storedRoles) {
      try {
        setSelectedRoleBySection(JSON.parse(storedRoles));
      } catch {
        // ignore
      }
    }
  }, []);

  // Persist selectedMediaBySection
  useEffect(() => {
    sessionStorage.setItem(
      "templateChapterMediaState",
      JSON.stringify(selectedMediaBySection)
    );
  }, [selectedMediaBySection]);

  // Persist selectedImage
  useEffect(() => {
    sessionStorage.setItem("templateChapterSelectedImage", selectedImage);
  }, [selectedImage]);

  // Persist role state
  useEffect(() => {
    sessionStorage.setItem(
      "templateChapterRoleState",
      JSON.stringify(selectedRoleBySection)
    );
  }, [selectedRoleBySection]);

  // Fetch teamId from Firestore
  useEffect(() => {
    const fetchUserTeamId = async uid => {
      try {
        const userDocRef = doc(db, "users", uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          if (data.teamId) {
            setUserTeamId(data.teamId);
          } else {
            setUserTeamId(null);
          }
        } else {
          console.error("User document does not exist");
        }
      } catch (error) {
        console.error("Error fetching user team ID:", error);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, user => {
      if (user) {
        fetchUserTeamId(user.uid);
      } else {
        setUserTeamId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Page transition effect
  useEffect(() => {
    document.body.classList.add("no-transition");
    const pageContainer = document.querySelector(".page__container");
    pageContainer && pageContainer.classList.add("no-transition");

    const handleLoad = () => {
      document.body.classList.remove("no-transition");
      pageContainer && pageContainer.classList.remove("no-transition");
    };

    window.addEventListener("load", handleLoad);
    return () => {
      window.removeEventListener("load", handleLoad);
    };
  }, []);

  // Core accordion toggle logic
  const handleAccordionToggle = (accordionId, type) => {
    setOpenAccordions(prev => {
      const isOpen = !prev[accordionId];
      const updated = { ...prev, [accordionId]: isOpen };
      sessionStorage.setItem(
        "templateChapterAccordionState",
        JSON.stringify(updated)
      );

      // Optional: preload media when opening
      if (isOpen && mediaSources[accordionId]) {
        const entries = Object.values(mediaSources[accordionId]);
        if (entries[0]) {
          const v0 = document.createElement("video");
          v0.src = entries[0].src;
          v0.preload = "auto";
          v0.load();
        }
        entries.slice(1).forEach(({ src }) => {
          const doMeta = () => {
            const v = document.createElement("video");
            v.src = src;
            v.preload = "metadata";
            v.load();
          };
          if ("requestIdleCallback" in window) {
            requestIdleCallback(doMeta, { timeout: 2000 });
          } else {
            setTimeout(doMeta, 2000);
          }
        });
      }

      if (isOpen) {
        if (type === "image") {
          setSelectedImage("image1");
        }

        if (type === "media" && mediaSources[accordionId]) {
          const firstKey = Object.keys(mediaSources[accordionId])[0];
          if (firstKey) {
            setSelectedMediaBySection(prevMedia => ({
              ...prevMedia,
              [accordionId]: firstKey
            }));
          }
        }

        // Ensure a default role is selected when section opens
        setSelectedRoleBySection(prevRoles => ({
          ...prevRoles,
          [accordionId]: prevRoles[accordionId] || "general"
        }));
      }

      return updated;
    });
  };

  const renderMediaButtons = sectionId => {
    const sectionMedia = mediaSources[sectionId];
    if (!sectionMedia) return null;

    return (
      <>
        <button
          type="button"
          onClick={() => setIsMovementGuideOpen(true)}
          className="movement-guide-question-button"
        >
          ?
        </button>
        {Object.entries(sectionMedia).map(([key, { label }]) => (
          <button
            key={key}
            onClick={() =>
              setSelectedMediaBySection(prev => ({
                ...prev,
                [sectionId]: key
              }))
            }
            className={selectedMediaBySection[sectionId] === key ? "active" : ""}
          >
            {label}
          </button>
        ))}
      </>
    );
  };

  const renderSelectedMedia = sectionId => {
    const selectedKey = selectedMediaBySection[sectionId];
    const sectionMedia = mediaSources[sectionId];

    if (!selectedKey || !sectionMedia || !sectionMedia[selectedKey]) return null;

    const { type, src, props = {} } = sectionMedia[selectedKey];

    if (type === "image") {
      return <img key={selectedKey} src={src} alt={selectedKey} {...props} />;
    }

    if (type === "video") {
      return (
        <LazyVideo
          key={selectedKey}
          src={src}
          preload="auto"
          forceLoad={true}
          {...props}
        />
      );
    }

    return null;
  };

  const renderRoleButtons = sectionId => {
    const currentRole = selectedRoleBySection[sectionId] || "general";

    return (
      <div className="role-selector">
        {ROLES.map(roleKey => {
          const label =
            roleKey === "general"
              ? "General"
              : `Agent ${roleKey.replace("agent", "")}`;
          const isActive = currentRole === roleKey;
          return (
            <button
              key={roleKey}
              type="button"
              className={isActive ? "active" : ""}
              onClick={() =>
                setSelectedRoleBySection(prev => ({
                  ...prev,
                  [sectionId]: roleKey
                }))
              }
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  };

  const getTextForSection = sectionId => {
    const mediaKey = selectedMediaBySection[sectionId];
    if (!mediaKey) return "Select a media option above to see detailed notes.";

    const roleKey = selectedRoleBySection[sectionId] || "general";

    const sectionText = textContent[sectionId];
    if (!sectionText) {
      return "No text content configured yet for this section. Add entries to textContent in TemplateChapter.js.";
    }

    const mediaText = sectionText[mediaKey];
    if (!mediaText) {
      return "No text content configured for this media option yet.";
    }

    const specificText =
      mediaText[roleKey] || mediaText.general || "No text for this role yet.";

    return specificText;
  };

  return (
    <div className={`page__container ${isBlurred ? "blurred-content" : ""}`}>
      <MovementGuideModal
        isOpen={isMovementGuideOpen}
        onClose={() => setIsMovementGuideOpen(false)}
      />
      <Highlight />

      <main
        className={`Main__content__container ${
          isBlurred ? "blurred-content" : ""
        }`}
      >
        {/* TEMPLATE_PAGE_TITLE → search/replace */}
        <h1 className="M-A-Title">TEMPLATE_PAGE_TITLE</h1>

        <ul className="accordian">
          {/* ==================================
              DEFENSE MAIN (EXAMPLE)
              ================================== */}
          <li>
            <input
              type="checkbox"
              name="accordion-defense"
              id="Template-Defense-Main"
              checked={!!openAccordions["Template-Defense-Main"]}
              onChange={() => handleAccordionToggle("Template-Defense-Main")}
            />
            <label htmlFor="Template-Defense-Main">
              Template Defense (Example)
            </label>

            <div className="accordian__content">
              <ul>
                {/* --- Defense Example Sub-Accordion --- */}
                <li>
                  <input
                    type="checkbox"
                    name="Defense-sub"
                    id="defense-example-accordion"
                    checked={!!openAccordions["defense-example-accordion"]}
                    onChange={() =>
                      handleAccordionToggle("defense-example-accordion", "media")
                    }
                  />
                  <label htmlFor="defense-example-accordion">
                    Defense Example Accordion
                    <Favorites
                      headingId="defense-example-accordion"
                      headingContent="Defense Example Accordion"
                      pageLocation="TEMPLATE_PAGE_ROUTE"
                      teamId={userTeamId}
                    />
                  </label>

                  <div
                    className="accordian__content"
                    data-container-id="TEMPLATE-HIGHLIGHT-DEF-EXAMPLE"
                  >
                    <div className="image-selector">
                      {renderMediaButtons("defense-example-accordion")}
                    </div>

                    <div className="retake-content__wrapper">
                      <div className="image__container">
                        <div
                          className="Image-Border image-hover-effect"
                          data-zoom-id="defense-example-accordion"
                        >
                          {renderSelectedMedia("defense-example-accordion")}
                        </div>
                      </div>

                      <div className="retake-text__container">
                        {/* Role buttons */}
                        {renderRoleButtons("defense-example-accordion")}
                        {/* Dynamic text depending on media + role */}
                        <p>{getTextForSection("defense-example-accordion")}</p>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </li>

          {/* ==================================
              ATTACK MAIN (EXAMPLE)
              ================================== */}
          <li>
            <input
              type="checkbox"
              name="accordion-attack"
              id="Template-Attack-Main"
              checked={!!openAccordions["Template-Attack-Main"]}
              onChange={() => handleAccordionToggle("Template-Attack-Main")}
            />
            <label htmlFor="Template-Attack-Main">
              Template Attack (Example)
            </label>

            <div className="accordian__content">
              <ul>
                {/* --- Attack Example Sub-Accordion --- */}
                <li>
                  <input
                    type="checkbox"
                    name="Attack-sub"
                    id="attack-example-accordion"
                    checked={!!openAccordions["attack-example-accordion"]}
                    onChange={() =>
                      handleAccordionToggle("attack-example-accordion", "media")
                    }
                  />
                  <label htmlFor="attack-example-accordion">
                    Attack Example Accordion
                    <Favorites
                      headingId="attack-example-accordion"
                      headingContent="Attack Example Accordion"
                      pageLocation="TEMPLATE_PAGE_ROUTE"
                      teamId={userTeamId}
                    />
                  </label>

                  <div
                    className="accordian__content"
                    data-container-id="TEMPLATE-HIGHLIGHT-ATK-EXAMPLE"
                  >
                    <div className="image-selector">
                      {renderMediaButtons("attack-example-accordion")}
                    </div>

                    <div className="retake-content__wrapper">
                      <div className="image__container">
                        <div
                          className="Image-Border image-hover-effect"
                          data-zoom-id="attack-example-accordion"
                        >
                          {renderSelectedMedia("attack-example-accordion")}
                        </div>
                      </div>

                      <div className="retake-text__container">
                        {/* Role buttons */}
                        {renderRoleButtons("attack-example-accordion")}
                        {/* Dynamic text depending on media + role */}
                        <p>{getTextForSection("attack-example-accordion")}</p>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </main>

      <section className="footer-section">
        <Footer />
      </section>
    </div>
  );
};

export default TemplateChapter;
